<template>
  <div class="bg-primary border border-secondary rounded-md overflow-hidden">
    <div class="p-4 flex flex-col gap-3">
      <DateSelectModal
        :title="$t('components.parts.expiration_date.title')"
        :description="$t('components.parts.expiration_date.description')"
        :initialState="expirationDate"
        :minDate="notificationDate"
        @update="updateExpirationDate"
      >
        <div
          class="flex flex-row gap-2 items-center justify-start flex-wrap text-style-base md:text-style-sm"
          :class="
            readonly ? 'hover:cursor-not-allowed' : 'hover:cursor-pointer'
          "
          @click="readonly ? $event.stopPropagation() : $emit('click')"
        >
          <Icon name="clock-default" class="fg-quaternary" />
          <span
            class="flex-1 text-left rtl:text-right flex items-center text-secondary font-medium shrink-0 whitespace-nowrap"
          >
            {{ $t("components.parts.expiration_date.title") }}
          </span>
          <DueDate
            :dueDate="expirationDate"
            :placeholder="$t('components.buttons.set_date')"
            :size="maxSmall ? 'lg' : 'md'"
            class="justify-self-end shrink-0 whitespace-nowrap"
          />
        </div>
      </DateSelectModal>
      <DateSelectModal
        v-if="expirationDate"
        :title="$t('components.parts.expiration_date.notification_date_title')"
        :description="
          $t('components.parts.expiration_date.notification_date_description')
        "
        :initialState="notificationDate"
        :minDate="new Date()"
        :maxDate="expirationDate"
        @update="updateNotificationDate"
      >
        <div
          class="flex flex-row gap-2 items-center justify-start flex-wrap text-base md:text-sm"
          :class="
            readonly ? 'hover:cursor-not-allowed' : 'hover:cursor-pointer'
          "
          @click="readonly ? $event.stopPropagation() : $emit('click')"
        >
          <Icon name="bell-default" class="fg-quaternary" />
          <span
            class="flex-1 text-left rtl:text-right flex items-center text-secondary font-medium shrink-0 whitespace-nowrap"
          >
            {{ $t("components.parts.expiration_date.notification_date_title") }}
          </span>
          <DueDate
            :dueDate="notificationDate"
            :size="maxSmall ? 'lg' : 'md'"
            :placeholder="$t('components.buttons.set_date')"
            class="justify-self-end shrink-0 whitespace-nowrap"
          />
        </div>
      </DateSelectModal>
    </div>
    <div
      v-if="expirationDate"
      class="border-t border-secondary bg-secondary flex gap-2 py-3 px-4"
    >
      <Icon name="info-circle" class="fg-quaternary mt-0.5" />
      <div class="text-style-base md:text-style-sm">
        <p class="text-primary font-medium">
          {{ $t("components.parts.expiration_date.opt_in_label") }}
        </p>
        <p class="text-tertiary font-normal">
          {{ $t("components.parts.expiration_date.opt_in_hint") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
// Import Vue Utitlies
import { inject } from "vue";

// Import Utitlies
import axios from "axios";
import { useI18n } from "vue-i18n";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";

// Import Components
import { DueDate } from "@/SharedComponents/Dates";
import { Icon } from "@/SharedComponents/Icon";
import { DateSelectModal } from "@/SharedComponents/Modals";
import { toast } from "@/SharedComponents/Toaster";

// Import Components
import { useBreakpoints } from "@/Composables/useBreakpoints";

// Breakpoints
const { maxSmall } = useBreakpoints();

// Translations
const { t } = useI18n();

// Emitter
const emitter = inject("emitter");

// Define Props
const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
  expirationDate: {
    type: [String, Date],
    default: undefined,
  },
  notificationDate: {
    type: [String, Date],
    default: undefined,
  },
  readonly: {
    type: Boolean,
    required: true,
  },
});

// Functions
const updateExpirationDate = (value) => {
  axios
    .post(route("api.files.expiration_date"), {
      note_ids: [props.file.id],
      expiration_date: value,
    })
    .then((response) => {
      // Show succes toast
      toast.success(t("toasts.files.update.success"));

      // Update files
      emitter.emit("update-files-data", response.data);

      // Reload the router
      router.reload();
    })
    .catch((error) => {
      console.log(error);
      if (error.response.status == 403) {
        toast.error(t("toasts.files.update.403"));
      } else {
        toast.error(
          error.response.data.message || t("toasts.files.update.error")
        );
      }
    });
};

const updateNotificationDate = (value) => {
  axios
    .post(route("api.files.reminder_date"), {
      note_ids: [props.file.id],
      reminder_date: value,
    })
    .then((response) => {
      // Show succes toast
      toast.success(t("toasts.files.update.success"));

      // Update files
      emitter.emit("update-files-data", response.data);

      // Reload the router
      router.reload();
    })
    .catch((error) => {
      console.log(error);
      if (error.response.status == 403) {
        toast.error(t("toasts.files.update.403"));
      } else {
        toast.error(
          error.response.data.message || t("toasts.files.update.error")
        );
      }
    });
};
</script>
